import type { Country } from '@gozoki/api-types'

export const firstLetter = (str: string) => str.slice(0, 1)

export const includesLowerCase = (str: string, search: string) =>
  str.toLowerCase().includes(search.toLowerCase())

/** Replace comma with point, in order to parse French number notation */
export const toInternationalNumber = (str: string) => str.replace(',', '.')

export const isNumber = (value: string) => {
  const parsedValue = parseFloat(toInternationalNumber(value))
  if (!Number.isNaN(parsedValue)) {
    return true
  }
  return false
}

/** Parse French float notation */
export const parseFrenchFloat = (str: string) => parseFloat(toInternationalNumber(str))

/** Create a string from a price */
export const priceToString = (price: number, suffix = '') => {
  let strPrice = price.toFixed(2)
  strPrice = strPrice.replace('.', ',') // Use French notation

  return `${strPrice}€${suffix}`
}

export const capitalizeFirstLetter = (str: string) => str.charAt(0).toUpperCase() + str.slice(1)

/** Capitalise un segment : première lettre en majuscule, reste en minuscule (locale FR). */
const capitalizeSegment = (segment: string): string =>
  segment.length === 0
    ? segment
    : segment.charAt(0).toLocaleUpperCase('fr') + segment.slice(1).toLocaleLowerCase('fr')

/**
 * Formate un prénom : chaque segment (séparé par espace ou tiret) capitalisé,
 * séparateurs préservés. Ex : "ANNE-CHARLOTE" -> "Anne-Charlote", "paul marie" -> "Paul Marie".
 */
export const capitalizeName = (value: string): string =>
  value
    .split(/([\s-]+)/)
    .map((part) => (/^[\s-]+$/.test(part) ? part : capitalizeSegment(part)))
    .join('')

export const removeTrailingSlash = (str: string): string => {
  if (str.endsWith('/')) {
    return str.slice(0, -1)
  }
  return str
}

export const getCountryLabel = (country: Country): string => {
  switch (country) {
    case 'FRANCE': {
      return 'France'
    }
    default: {
      return country
    }
  }
}

/**
 * Parses the French department code embedded at the start of a store label
 * (e.g. "33 - Tourny" -> "33", "2A - Ajaccio" -> "2A").
 * Returns null if the label does not follow that convention.
 */
export const parseDepartmentFromStoreLabel = (label: string): string | null => {
  const match = /^\s*(2A|2B|\d{1,2})\s*-/i.exec(label)
  if (!match) {
    return null
  }
  return match[1].toUpperCase()
}

/**
 * Returns the French department code from a postal code, using its first two digits
 * (e.g. "31000" -> "31"). Returns null if the postal code isn't a 5-digit French code.
 */
export const getDepartmentCodeFromPostalCode = (postalCode: string): string | null => {
  if (!/^\d{5}$/.test(postalCode)) {
    return null
  }
  return postalCode.slice(0, 2)
}

/** Texte comparable pour une recherche : minuscules, sans accents (« Écully » → « ecully »). */
export const normalizeSearchText = (text: string): string =>
  text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()

export const normalizeCityName = (name: string): string =>
  normalizeSearchText(name).replace(/\s+/g, ' ').trim()

/** Initiales d'une personne (« SA » pour Saad Amjad), « ? » sans prénom ni nom. */
export const personInitials = (firstName: string, lastName: string): string => {
  const initials = `${firstName.trim().charAt(0)}${lastName.trim().charAt(0)}`.toUpperCase()
  return initials === '' ? '?' : initials
}
